/**
 * How a purchase order reached its supplier, as one line in the page header on
 * every tab (Ben, 10 Oct, in place of mock-up 11's panel): "Sent by link 10 Oct
 * · opened 11:40 · accept by 13 Oct", or "… · accepted 11 Oct" once accepted,
 * with *Resend* and *Revoke* only while the view offers them. Revoking asks
 * first: every emailed link stops working. The sentence is the view's.
 */
import { useState } from "react";
import type { SentToSupplier } from "@shared-core/data/order-to-invoice-types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Button } from "../../primitives/button";

export function SentToSupplierLine({
  sent,
  isBusy,
  onResend,
  onRevoke,
}: {
  sent: SentToSupplier;
  isBusy: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const [isRevoking, setIsRevoking] = useState(false);
  return (
    <div
      role="group"
      aria-label="Sent to supplier"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground"
    >
      <span title={sent.sent_to ?? undefined}>{sent.header_label}</span>
      {sent.resend_url && (
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={isBusy}
          onClick={onResend}
        >
          Resend
        </Button>
      )}
      {sent.revoke_url && (
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={isBusy}
          onClick={() => setIsRevoking(true)}
        >
          Revoke
        </Button>
      )}
      <AlertDialog open={isRevoking} onOpenChange={setIsRevoking}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke every emailed link?</AlertDialogTitle>
            <AlertDialogDescription>
              The links sent to {sent.sent_to ?? "the supplier"} stop working at
              once. A supplier who signs in still reaches the order; Resend
              sends a new link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the link</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsRevoking(false);
                onRevoke();
              }}
            >
              Revoke link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
